import { useEffect, useState } from "react";
import { toDate, fmtDateTime, fmtDateTimeSec } from "@/lib/datetime";
import { toast } from "sonner";
import api from "@/lib/api";
import { CircleNotch, ArrowsClockwise, CheckCircle, Warning, Clock, Broadcast, CaretDown, CaretUp, Storefront, ChatCircleText, WarningCircle, X } from "@phosphor-icons/react";

const KIND_LABELS = {
  availability: "Dostupnost",
  rate: "Cijena",
  restriction: "Ograničenja",
};

const REASON_LABELS = {
  reservation_create: "Nova rezervacija",
  reservation_update: "Izmjena rezervacije",
  reservation_delete: "Brisanje rezervacije",
  price_change: "Promjena cijene",
  rate_period_create: "Nova cjenovna sezona",
  rate_period_update: "Izmjena sezone",
  rate_period_delete: "Brisanje sezone",
  quick_set_price: "Brza izmjena cijene",
  quick_set_channel_price: "Cijena po kanalu",
  quick_set_min_stay: "Min. boravak",
  quick_set_max_stay: "Max. boravak",
  quick_set_stop_sell: "Zaustavi prodaju",
  manual_push_ari: "Ručni push",
  channex_booking_new: "Nova rezervacija iz OTA",
  channex_booking_update: "Update iz OTA",
  direct_public_booking: "Direktna rezervacija",
  integration_booking: "Booking Engine",
  integration_cancel: "Otkazivanje iz Booking Enginea",
};

// Channel-specific logos + brand accent colors used for badges.
const CHANNEL_META = {
  booking_com: { label: "Booking.com", initial: "B", tint: "#003580", type: "OTA" },
  airbnb: { label: "Airbnb", initial: "A", tint: "#FF385C", type: "OTA" },
  expedia: { label: "Expedia", initial: "E", tint: "#FFC72C", type: "OTA" },
  booking_engine: { label: "Booking Engine", initial: "BE", tint: "#4F46E5", type: "Direct" },
};

export default function ChannelsPage() {
  const [data, setData] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityError, setActivityError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resyncing, setResyncing] = useState(false);
  const [resyncMsg, setResyncMsg] = useState("");
  const [activityOpen, setActivityOpen] = useState(false);
  const [mappingTree, setMappingTree] = useState(null);
  const [mappingOpen, setMappingOpen] = useState(false);
  // Live OTA connectivity check state — populated only after user clicks
  // "Provjeri povezanost". Rate-limited server-side to 60 s per tenant.
  const [healthChecking, setHealthChecking] = useState(false);
  const [healthResults, setHealthResults] = useState(null); // { checked_at, results: {key: {status, message, ...}} }
  const [healthRetryAt, setHealthRetryAt] = useState(null); // ISO timestamp; null when no rate-limit active
  const [healthNow, setHealthNow] = useState(Date.now()); // heartbeat for countdown

  useEffect(() => {
    if (!healthRetryAt) return;
    const id = setInterval(() => setHealthNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [healthRetryAt]);

  const [connectingMsgs, setConnectingMsgs] = useState(false);
  const [diag, setDiag] = useState(null);
  const connectMessages = async () => {
    setConnectingMsgs(true);
    try {
      const { data: r } = await api.post("/channels/ota/connect-messages").catch((e) => ({ data: { errors: [{ property: "OTA", error: e?.response?.data?.detail || "Povezivanje nije uspjelo" }], created: [] } }));
      if (r.errors?.length) toast.error(`Greška za: ${r.errors.map((e) => e.property).join(", ")} — ${r.errors[0].error}`);
      else toast.success(r.created.length ? `OTA poruke povezane za: ${r.created.join(", ")}` : "OTA poruke su povezane za sve smještaje.");
      const { data: d } = await api.get("/inbox/diagnostics");
      setDiag(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dijagnostika poruka nije uspjela.");
    } finally {
      setConnectingMsgs(false);
    }
  };

  const loadStatus = () => api.get("/channels/status").then((r) => {
    setData(r.data);
    if (r.data.health) setHealthResults(r.data.health);
    return r.data;
  });

  const runHealthCheck = async (silent = false) => {
    setHealthChecking(true);
    try {
      const { data } = await api.post("/channels/channex/health-check");
      setHealthResults(data);
      setHealthRetryAt(data.next_allowed_at);
      loadStatus();
      if (!silent) toast.success("Provjera povezanosti završena.");
    } catch (e) {
      if (silent) return;
      if (e?.response?.status === 429) {
        toast.error(e.response.data.detail || "Prečesto — pričekaj minutu.");
        // Try to compute retry-at from Retry-After header
        const ra = parseInt(e.response.headers?.["retry-after"] || "0", 10);
        if (ra > 0) setHealthRetryAt(new Date(Date.now() + ra * 1000).toISOString());
      } else {
        toast.error(e?.response?.data?.detail || "Greška pri provjeri povezanosti.");
      }
    } finally {
      setHealthChecking(false);
    }
  };

  // Compute seconds until next check is allowed (0 = ready).
  const retrySeconds = (() => {
    if (!healthRetryAt) return 0;
    const diff = Math.max(0, Math.ceil((new Date(healthRetryAt).getTime() - healthNow) / 1000));
    if (diff === 0 && healthRetryAt) {
      // Clear the retry timer once it's expired.
      setTimeout(() => setHealthRetryAt(null), 0);
    }
    return diff;
  })();

  const fetchMappingTree = async () => {
    try {
      const { data } = await api.get("/channels/channex/mapping-tree");
      setMappingTree(data);
    } catch { setMappingTree(null); }
  };

  const fetchActivity = async () => {
    try {
      const { data } = await api.get("/channels/channex/activity");
      setActivity(data);
      setActivityError(null);
    } catch (e) {
      // Bugfix (2026-10-07): previously silently swallowed the error, which
      // hid the entire "Zadnja sinkronizacija" section when it couldn't load.
      // Now we keep an empty-safe shape so header is still rendered, and
      // surface the error reason to the user.
      // eslint-disable-next-line no-console
      console.error("fetchActivity failed:", e?.response?.status, e?.response?.data || e?.message);
      setActivity({ pending: [], log: [], summary: { success_count: 0, failed_count: 0, pending_count: 0 } });
      setActivityError(e?.response?.data?.detail || e?.message || "Nije bilo moguće učitati sync aktivnost.");
    }
  };

  const forceResyncAll = async () => {
    if (!window.confirm("Pokrenuti Full sync? Šalje 500 dana dostupnosti, cijena i restrikcija za sve povezane smještaje.")) return;
    setResyncing(true);
    setResyncMsg("");
    try {
      const { data: props } = await api.get("/properties");
      const mapped = props.filter((p) => (p.channel_mappings || []).some((m) => m.channel === "channex" && m.external_id));
      if (mapped.length === 0) {
        setResyncMsg("Nema smještaja koji su povezani s OTA sustavom.");
        return;
      }
      let units = 0;
      let failed = 0;
      for (const p of mapped) {
        try {
          const { data } = await api.post(`/channels/channex/full-sync/${p.id}`);
          units += data.units_enqueued || 0;
        } catch (e) { failed += 1; console.error(`full sync failed for ${p.name}:`, e); }
      }
      if (failed) toast.error(`Full sync nije uspio za ${failed} smještaj(a).`);
      setResyncMsg(`Full sync pokrenut: ${mapped.length} smještaj(a), ${units} jedinica, 500 dana unaprijed.`);
      setActivityOpen(true);
      setTimeout(fetchActivity, 500);
      setTimeout(fetchMappingTree, 1200);
    } catch (e) {
      setResyncMsg(`Greška: ${e?.response?.data?.detail || e.message}`);
    } finally { setResyncing(false); }
  };

  useEffect(() => {
    loadStatus().then((d) => {
      // Auto-refresh live OTA state when the cached check is older than 60 s
      const at = d?.health?.checked_at ? new Date(d.health.checked_at).getTime() : 0;
      if (Date.now() - at > 60000) runHealthCheck(true);
    }).finally(() => setLoading(false));
    fetchActivity();
    fetchMappingTree();
    const interval = setInterval(fetchActivity, 5000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="flex h-64 items-center justify-center"><CircleNotch size={28} className="animate-spin text-primary" /></div>;
  }

  const activePending = (activity?.pending || []).filter((t) => t.status === "pending" || t.status === "running");
  const displayLog = activity?.log || [];

  return (
    <div className="space-y-5" data-testid="channels-page">
      {/* Header */}
      <div className="rounded-lg border border-border bg-card p-4 sm:p-5">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="truncate text-lg font-semibold text-foreground">Povezanost s kanalima</h2>
              <span className="shrink-0 whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 font-mono text-[11px] font-semibold text-primary" data-testid="channels-unit-count">{data.total_units} jedinica</span>
            </div>
            <p className="mt-1 truncate text-sm text-muted-foreground" title="Sve promjene u PMS-u (cijene, dostupnost, restrikcije, rezervacije) automatski se šalju u sve povezane kanale.">
              Promjene iz PMS-a automatski se šalju u sve povezane kanale.
              {healthResults?.checked_at && <span className="text-[11px]" data-testid="health-last-checked"> · Provjera: {timeAgoHr(healthResults.checked_at)}</span>}
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2 overflow-x-auto no-scrollbar">
            <button
              onClick={() => runHealthCheck(false)}
              disabled={healthChecking || retrySeconds > 0}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              title={retrySeconds > 0 ? `Sljedeća provjera za ${retrySeconds}s (limit)` : "Provjerava live status svih OTA kanala (limit: 1× / 60 s)"}
              data-testid="health-check-btn"
            >
              {healthChecking ? <CircleNotch size={14} className="animate-spin" /> : <Broadcast size={14} weight="bold" />}
              {healthChecking ? "Provjeravam..." : retrySeconds > 0 ? `Provjeri (${retrySeconds}s)` : "Provjeri"}
            </button>
            <button
              onClick={connectMessages}
              disabled={connectingMsgs}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
              title="Uključuje primanje OTA poruka (Booking.com / Airbnb) u Inbox za sve povezane smještaje"
              data-testid="connect-messages-btn"
            >
              {connectingMsgs ? <CircleNotch size={14} className="animate-spin" /> : <ChatCircleText size={14} weight="bold" />}
              {connectingMsgs ? "Povezujem..." : "OTA poruke"}
            </button>
            <button
              onClick={forceResyncAll}
              disabled={resyncing}
              className="inline-flex h-9 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
              title="Full sync — pošalji 500 dana dostupnosti, cijena i restrikcija za sve povezane smještaje"
              data-testid="force-resync-btn"
            >
              {resyncing ? <CircleNotch size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
              {resyncing ? "Sinkroniziram..." : "Full sync"}
            </button>
          </div>
        </div>
        {resyncMsg && <p className="mt-3 rounded-md bg-muted px-3 py-2 text-xs" data-testid="resync-message">{resyncMsg}</p>}
      </div>
      {diag && <MessagingDiagnostics d={diag} onClose={() => setDiag(null)} />}

      {/* Channel cards — only Booking.com / Airbnb / Expedia / Booking Engine */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {data.channels.map((ch) => {
          const meta = CHANNEL_META[ch.key] || { label: ch.name, initial: (ch.name || "?").slice(0, 1), tint: "#6B7280", type: ch.type };
          // Prefer live health result if we have it; fall back to "configured" static state.
          const hc = healthResults?.results?.[ch.key];
          return (
            <div key={ch.key} className="flex flex-col rounded-lg border border-border bg-card p-4 lift sm:p-5" data-testid={`channel-${ch.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="flex min-w-0 items-center gap-3">
                  <div
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-sm font-bold text-white"
                    style={{ background: meta.tint }}
                  >
                    {ch.key === "booking_engine" ? <Storefront size={18} weight="fill" /> : meta.initial}
                  </div>
                  <div className="min-w-0">
                    <h3 className="truncate whitespace-nowrap text-sm font-semibold text-foreground">{meta.label}</h3>
                    <p className="truncate text-[10px] uppercase tracking-wider text-muted-foreground">{meta.type}</p>
                  </div>
                </div>
                {hc ? (
                  hc.status === "connected" ? (
                    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-success/10 px-2 py-0.5 text-[11px] font-medium text-success" title={hc.message} data-testid={`channel-health-${ch.key}`}>
                      <span className="relative flex h-2 w-2">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                      </span>
                      Spojeno
                    </span>
                  ) : hc.status === "disconnected" ? (
                    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-700" title={hc.message} data-testid={`channel-health-${ch.key}`}>
                      <Warning size={12} weight="fill" /> Neaktivno
                    </span>
                  ) : hc.status === "error" ? (
                    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-700" title={hc.message} data-testid={`channel-health-${ch.key}`}>
                      <Warning size={12} weight="fill" /> Greška
                    </span>
                  ) : (
                    <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground" title={hc.message} data-testid={`channel-health-${ch.key}`}>
                      Nije postavljen
                    </span>
                  )
                ) : ch.configured ? (
                  <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-primary/10 px-2 py-0.5 text-[11px] font-medium text-primary" title="Konfigurirano u PMS-u — klikni 'Provjeri povezanost' za live status">
                    <CheckCircle size={12} weight="fill" /> Konfigurirano
                  </span>
                ) : (
                  <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                    <Warning size={12} weight="regular" /> Nije povezano
                  </span>
                )}
              </div>
              <p className="mb-4 mt-3 line-clamp-2 text-xs text-muted-foreground">{ch.description}</p>
              {hc && hc.message && (
                <p className="-mt-2 mb-4 truncate rounded bg-muted/50 px-2 py-1 text-[10px] text-muted-foreground" title={hc.message} data-testid={`channel-health-msg-${ch.key}`}>{hc.message}</p>
              )}
              <div className="mt-auto flex items-center justify-between border-t border-border pt-3">
                <span className="mt-0 text-xs text-muted-foreground">
                  Povezane jedinice: <span className="font-mono font-semibold text-foreground">{ch.mapped_units}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Channex Mapping Tree — property → room types → rate plans with IDs */}
      {mappingTree && (
        <ChannexMappingSection
          tree={mappingTree}
          open={mappingOpen}
          onToggle={() => setMappingOpen((v) => !v)}
          onRefresh={fetchMappingTree}
        />
      )}

      {/* Sync Activity — collapsed by default. Always rendered (even on error)
          so user never loses sight of the sync section; defensive guards on
          summary + log fields handle partial payloads gracefully. */}
      <div className="rounded-lg border border-border bg-card" data-testid="sync-activity-card">
          <button
            type="button"
            onClick={() => setActivityOpen((v) => !v)}
            className="flex w-full items-center justify-between border-b border-border px-5 py-3 text-left hover:bg-muted/40"
            data-testid="sync-activity-toggle"
          >
            <div>
              <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
                <Broadcast size={14} weight="duotone" className="text-primary" />
                Zadnja sinkronizacija
              </h3>
              <p className="text-xs text-muted-foreground">
                {activityError
                  ? "Greška učitavanja — klikni za detalje"
                  : `Auto-refresh svakih 5s · klikni za ${activityOpen ? "sakrivanje" : "otvaranje"} detalja`}
              </p>
            </div>
            <div className="flex items-center gap-3 text-xs">
              {activePending.length > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 font-medium text-warning">
                  <CircleNotch size={10} className="animate-spin" /> {activePending.length} u redu
                </span>
              )}
              <span className="inline-flex items-center gap-1 text-success">
                <CheckCircle size={12} weight="fill" /> {activity?.summary?.success_count ?? 0}
              </span>
              {(activity?.summary?.failed_count ?? 0) > 0 && (
                <span className="inline-flex items-center gap-1 text-destructive">
                  <Warning size={12} weight="fill" /> {activity.summary.failed_count}
                </span>
              )}
              {activityOpen ? <CaretUp size={14} weight="bold" /> : <CaretDown size={14} weight="bold" />}
            </div>
          </button>

          {activityOpen && (
            <div className="max-h-96 overflow-y-auto">
              {activityError ? (
                <div className="p-5 text-sm">
                  <p className="font-semibold text-destructive">Nije bilo moguće učitati sync aktivnost.</p>
                  <p className="mt-1 text-muted-foreground">Razlog: <code className="rounded bg-muted px-1.5 py-0.5 text-xs">{activityError}</code></p>
                  <p className="mt-2 text-xs text-muted-foreground">Otvori Chrome DevTools → Console za više detalja, ili pošalji screenshot administratoru.</p>
                </div>
              ) : activePending.length === 0 && displayLog.length === 0 ? (
                <p className="p-6 text-center text-sm text-muted-foreground">Nema sync aktivnosti. Promjena cijene, restrikcije ili rezervacije pojavit će se ovdje.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-muted/80 text-muted-foreground">
                    <tr>
                      <th className="px-4 py-2 text-left font-semibold">Status</th>
                      <th className="px-4 py-2 text-left font-semibold">Tip</th>
                      <th className="px-4 py-2 text-left font-semibold">Jedinica</th>
                      <th className="px-4 py-2 text-left font-semibold">Razlog</th>
                      <th className="px-4 py-2 text-left font-semibold">Task ID</th>
                      <th className="px-4 py-2 text-left font-semibold">Poruka</th>
                      <th className="px-4 py-2 text-right font-semibold">Vrijeme</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {activePending.map((t) => (
                      <tr key={t.id} className="hover:bg-muted/30" data-testid={`activity-pending-${t.id}`}>
                        <td className="px-4 py-1.5">
                          <span className="inline-flex items-center gap-1 text-warning">
                            {t.status === "running" ? <CircleNotch size={10} className="animate-spin" /> : <Clock size={10} />}
                            {t.status === "running" ? "šaljem..." : "čeka"}
                          </span>
                        </td>
                        <td className="px-4 py-1.5 text-foreground">{KIND_LABELS[t.kind] || t.kind}</td>
                        <td className="px-4 py-1.5 font-mono">{t.unit_code}</td>
                        <td className="px-4 py-1.5 text-muted-foreground">{REASON_LABELS[t.reason] || t.reason}</td>
                        <td className="px-4 py-1.5 text-muted-foreground">—</td>
                        <td className="px-4 py-1.5 text-muted-foreground">{t.last_error || "—"}</td>
                        <td className="px-4 py-1.5 text-right font-mono text-[10px] text-muted-foreground">{fmtDateTimeSec(t.created_at)}</td>
                      </tr>
                    ))}
                    {displayLog.map((l) => {
                      const isCertCandidate = (l.reason || "").startsWith("quick_set_") && (l.channex_task_ids || []).length > 0;
                      const coalesced = l.coalesced_task_count || 0;
                      const isDelta = l.dates_scope === "delta";
                      const valCount = l.value_count || 0;
                      const isInbound = l.direction === "inbound";
                      const inboundKindLabel = {
                        booking_new: "📥 Nova rezervacija",
                        booking_modified: "📥 Izmjena rezervacije",
                        booking_cancel: "📥 Otkazana rezervacija",
                        webhook_raw: "📥 Raw webhook",
                      }[l.kind] || l.kind;
                      return (
                      <tr key={l.id} className={`hover:bg-muted/30 ${isInbound ? "bg-blue-500/5 border-l-2 border-blue-500" : ""}`} data-testid={`activity-log-${l.id}`}>
                        <td className="px-4 py-1.5">
                          {isInbound ? (
                            <span className="inline-flex items-center gap-1 text-blue-600" title="Ulazni event iz OTA (OTA → PMS)">
                              <ArrowsClockwise size={10} weight="fill" /> ulaz
                            </span>
                          ) : l.status === "success" ? (
                            <span className="inline-flex items-center gap-1 text-success"><CheckCircle size={10} weight="fill" /> uspjeh</span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-destructive"><Warning size={10} weight="fill" /> greška</span>
                          )}
                        </td>
                        <td className="px-4 py-1.5 text-foreground">
                          <div className="flex items-center gap-1.5">
                            <span>{isInbound ? inboundKindLabel : (KIND_LABELS[l.kind] || l.kind)}</span>
                            {isCertCandidate && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded bg-emerald-600 px-1.5 py-0.5 text-[9px] font-bold uppercase text-white"
                                title="Primarni sync ID — kopiraj ga u formu"
                                data-testid={`cert-badge-${l.id}`}
                              >
                                🎯 CERT
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-1.5 font-mono">{l.unit_code}</td>
                        <td className="px-4 py-1.5 text-muted-foreground">{isInbound ? (l.status === "pending" ? "ACK pending" : "ACK'd") : (REASON_LABELS[l.reason] || l.reason)}</td>
                        <td className="px-4 py-1.5">
                          {isInbound ? (
                            l.revision_id ? (
                              <TaskIdCell taskIds={[l.revision_id]} highlight={false} />
                            ) : <span className="text-muted-foreground">—</span>
                          ) : (
                            <TaskIdCell taskIds={l.channex_task_ids || []} highlight={isCertCandidate} />
                          )}
                        </td>
                        <td className="px-4 py-1.5 text-muted-foreground">
                          <div className="flex flex-col gap-0.5">
                            <span>{l.message}</span>
                            {isInbound && l.booking_id && (
                              <span className="font-mono text-[10px] text-muted-foreground/70">booking: {l.booking_id}</span>
                            )}
                            {(coalesced > 1 || isDelta || valCount > 0) && (
                              <span className="text-[10px] text-muted-foreground/70">
                                {coalesced > 1 && <span className="mr-2">📦 {coalesced} akcija spojeno</span>}
                                {isDelta && <span className="mr-2">🎯 delta {(l.dates || []).length} dan{(l.dates || []).length === 1 ? "" : "a"}</span>}
                                {!isDelta && l.dates_scope === "full_horizon" && <span className="mr-2">📅 puni 365-dnevni sync</span>}
                                {valCount > 0 && <span>{valCount} redova</span>}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-1.5 text-right font-mono text-[10px] text-muted-foreground">{fmtDateTimeSec(l.completed_at)}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </div>
    </div>
  );
}

function TaskIdCell({ taskIds, highlight = false }) {
  const [copied, setCopied] = useState(null);
  if (!taskIds || taskIds.length === 0) return <span className="text-muted-foreground">—</span>;

  const copy = (id) => {
    navigator.clipboard.writeText(id).then(() => {
      setCopied(id);
      setTimeout(() => setCopied(null), 1200);
    });
  };
  return (
    <div className="flex flex-col gap-0.5">
      {taskIds.map((id, idx) => {
        const isPrimary = highlight && idx === 0;
        return (
          <button
            key={id}
            onClick={() => copy(id)}
            className="group flex items-center gap-1.5 text-left"
            title={isPrimary ? "Primarni sync ID — klikni za kopiranje" : "Klikni za kopiranje"}
            data-testid={`task-id-${id}`}
          >
            <code
              className={
                isPrimary
                  ? "max-w-[220px] truncate rounded bg-emerald-600/15 px-1.5 py-0.5 font-mono text-[10px] font-semibold text-emerald-700 ring-1 ring-emerald-500/40 group-hover:bg-emerald-600/25 dark:text-emerald-300"
                  : "max-w-[220px] truncate rounded bg-muted/60 px-1.5 py-0.5 font-mono text-[10px] text-foreground group-hover:bg-primary/10 group-hover:text-primary"
              }
            >
              {id}
            </code>
            {copied === id && <span className="text-[10px] text-success">✓ kopirano</span>}
          </button>
        );
      })}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Channex Mapping Tree — displays every PMS entity with its Channex UUID so
// the user can copy them into the certification form (or channel mappings).
// Highlights entities that DIDN'T sync so problems are obvious at a glance.
// -----------------------------------------------------------------------------
function ChannexMappingSection({ tree, open, onToggle, onRefresh }) {
  const props = tree?.properties || [];
  const totalProps = props.length;
  const syncedProps = props.filter((p) => p.synced).length;
  const totalRTs = props.reduce((n, p) => n + (p.room_types?.length || 0), 0);
  const syncedRTs = props.reduce((n, p) => n + (p.room_types || []).filter((r) => r.synced).length, 0);
  const totalRPs = props.reduce((n, p) => n + (p.room_types || []).reduce((m, r) => m + (r.rate_plans?.length || 0), 0), 0);
  const syncedRPs = props.reduce((n, p) => n + (p.room_types || []).reduce((m, r) => m + (r.rate_plans || []).filter((rp) => rp.synced).length, 0), 0);
  const allSynced = totalProps === syncedProps && totalRTs === syncedRTs && totalRPs === syncedRPs && totalProps > 0;

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="channex-mapping-section">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between gap-3 border-b border-border px-4 py-3 text-left hover:bg-muted/40 sm:px-5"
        data-testid="channex-mapping-toggle"
      >
        <div>
          <h3 className="flex items-center gap-2 whitespace-nowrap text-sm font-semibold text-foreground">
            <Broadcast size={14} weight="duotone" className="text-primary" />
            Identifikatori
          </h3>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium ${allSynced ? "bg-success/10 text-success" : "bg-warning/10 text-warning"}`}>
            {allSynced
              ? <><CheckCircle size={12} weight="fill" /> Sve povezano</>
              : <><Warning size={12} weight="fill" /> {syncedProps + syncedRTs + syncedRPs}/{totalProps + totalRTs + totalRPs}</>}
          </span>
          {open ? <CaretUp size={14} /> : <CaretDown size={14} />}
        </div>
      </button>

      {open && (
        <div className="space-y-4 p-3 sm:p-5">
          {props.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nema objekata. Kreiraj smještaj u <span className="font-medium">Smještaji</span> stranici — property se automatski povezuje.</p>
          ) : (
            props.map((p) => (
              <PropertyMappingCard
                key={p.pms_id}
                property={p}
                onRefresh={onRefresh}
              />
            ))
          )}
        </div>
      )}
    </div>
  );
}

function CopyIdChip({ label, value, muted = false }) {
  const [copied, setCopied] = useState(false);
  if (!value) {
    return (
      <span className="inline-flex items-center gap-1 whitespace-nowrap rounded bg-warning/10 px-1.5 py-0.5 text-[10px] font-medium text-warning" title={`${label}: nije sinkroniziran`}>
        <Warning size={10} weight="fill" /> {label}: —
      </span>
    );
  }
  const copy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      title={`Klikni za kopiranje: ${value}`}
      className={`group inline-flex items-center gap-1 whitespace-nowrap rounded px-1.5 py-0.5 text-[10px] font-mono transition ${muted ? "bg-muted/60 text-muted-foreground hover:bg-primary/10 hover:text-primary" : "bg-emerald-600/15 text-emerald-700 ring-1 ring-emerald-500/40 hover:bg-emerald-600/25 dark:text-emerald-300"}`}
      data-testid={`copy-${label.toLowerCase()}-${value}`}
    >
      <span className="text-[9px] uppercase tracking-wide opacity-60 group-hover:opacity-100">{label}</span>
      <span>{value.slice(0, 8)}…{value.slice(-4)}</span>
      {copied && <span className="text-[10px]">✓</span>}
    </button>
  );
}

function PropertyMappingCard({ property, onRefresh }) {
  const [expanded, setExpanded] = useState(true);
  const [pushing, setPushing] = useState(false);

  const forceSync = async () => {
    setPushing(true);
    try {
      await api.post(`/channels/channex/sync-property/${property.pms_id}`);
      // Small delay then refresh
      setTimeout(() => { onRefresh?.(); setPushing(false); }, 800);
    } catch (e) {
      console.error("force sync failed:", e);
      setPushing(false);
    }
  };

  return (
    <div className="rounded-md border border-border bg-background" data-testid={`mapping-property-${property.pms_id}`}>
      <div className="flex items-start justify-between gap-3 border-b border-border px-3 py-2.5 sm:px-4">
        <div className="flex-1 min-w-0">
          <div className="flex min-w-0 items-center gap-2">
            <button
              onClick={() => setExpanded((v) => !v)}
              className="flex min-w-0 items-center gap-1 truncate whitespace-nowrap text-sm font-semibold text-foreground hover:text-primary"
            >
              {expanded ? <CaretDown size={12} /> : <CaretUp size={12} className="rotate-180" />}
              {property.name}
            </button>
            <span className="hidden rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground sm:inline">{property.currency}</span>
            <span className="hidden rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground sm:inline">{property.country}</span>
            {property.synced ? (
              <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success">
                <CheckCircle size={10} weight="fill" /> <span className="hidden sm:inline">Povezan</span>
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive">
                <Warning size={10} weight="fill" /> Nije povezan
              </span>
            )}
          </div>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <CopyIdChip label="PMS" value={property.pms_id} muted />
            <CopyIdChip label="OTA ID" value={property.channex_id} />
          </div>
        </div>
        {!property.synced && (
          <button
            type="button"
            onClick={forceSync}
            disabled={pushing}
            className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-40"
            data-testid={`force-sync-property-${property.pms_id}`}
          >
            {pushing ? <CircleNotch size={12} className="inline animate-spin" /> : <>Pošalji na kanale</>}
          </button>
        )}
      </div>

      {expanded && (
        <div className="px-4 py-2 space-y-2">
          {property.room_types.length === 0 ? (
            <p className="text-xs text-muted-foreground py-2">Nema tipova soba za ovaj objekt.</p>
          ) : (
            property.room_types.map((rt) => (
              <div key={rt.pms_id} className="rounded border border-border/60 bg-muted/20 px-3 py-2" data-testid={`mapping-rt-${rt.pms_id}`}>
                <div className="flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate whitespace-nowrap text-sm font-medium text-foreground">{rt.name}</span>
                  <span className="hidden shrink-0 whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground sm:inline">occ: {rt.occupancy}</span>
                  <span className="shrink-0 whitespace-nowrap rounded bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">{rt.count_of_rooms} jed.</span>
                  {rt.synced
                    ? <span className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-success/10 px-1.5 py-0.5 text-[10px] font-medium text-success"><CheckCircle size={10} weight="fill" /> OK</span>
                    : <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-[10px] font-medium text-destructive"><Warning size={10} weight="fill" /> Nije synced</span>}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2">
                  <CopyIdChip label="PMS" value={rt.pms_id} muted />
                  <CopyIdChip label="OTA ID" value={rt.channex_id} />
                </div>
                {rt.rate_plans && rt.rate_plans.length > 0 && (
                  <div className="mt-2 space-y-1 border-l-2 border-primary/20 pl-3">
                    {rt.rate_plans.map((rp) => (
                      <div key={rp.pms_id} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`mapping-rp-${rp.pms_id}`}>
                        <span className="whitespace-nowrap font-medium text-foreground">
                          {rp.is_default && <span className="text-primary">★ </span>}
                          {rp.name}
                        </span>
                        {rp.code && <span className="rounded bg-muted/60 px-1 py-0.5 text-[9px] font-mono text-muted-foreground">{rp.code}</span>}
                        <CopyIdChip label="PMS" value={rp.pms_id} muted />
                        <CopyIdChip label="OTA ID" value={rp.channex_id} />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

function timeAgoHr(iso) {
  const min = Math.floor((Date.now() - toDate(iso).getTime()) / 60000);
  if (min < 1) return "upravo sada";
  if (min < 60) return `prije ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `prije ${h} h`;
  return fmtDateTime(iso);
}

const fmtAt = (iso) => (iso ? new Date(iso).toLocaleString("hr-HR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "nikad");

function DiagRow({ ok, label, detail, testId }) {
  return (
    <div className="flex items-start gap-2 py-1.5" data-testid={testId}>
      {ok ? <CheckCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-emerald-600" /> : <WarningCircle size={16} weight="fill" className="mt-0.5 shrink-0 text-red-600" />}
      <div className="min-w-0">
        <p className="text-xs font-semibold text-foreground">{label}</p>
        {detail && <p className="break-words text-[11px] text-muted-foreground">{detail}</p>}
      </div>
    </div>
  );
}

function MessagingDiagnostics({ d, onClose }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4" data-testid="messaging-diagnostics">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-foreground">Dijagnostika OTA poruka</h3>
        <button onClick={onClose} className="rounded p-1 text-muted-foreground hover:bg-muted" data-testid="messaging-diagnostics-close"><X size={14} /></button>
      </div>
      <p className="mb-2 break-all font-mono text-[10.5px] text-muted-foreground">{d.channex_base_url} → {d.callback_url || "WEBHOOK_BASE_URL nije postavljen"}</p>
      <div className="divide-y divide-border">
        {d.checks.map((c, i) => <DiagRow key={i} {...c} testId={`diag-check-${i}`} />)}
        <DiagRow ok={!!d.last_message_webhook_at} label="Zadnja primljena poruka (webhook)" detail={`${fmtAt(d.last_message_webhook_at)} · zadnji bilo koji webhook: ${fmtAt(d.last_any_webhook_at)}`} testId="diag-last-webhook" />
        {d.properties.map((p) => (
          <DiagRow key={p.channex_property_id} ok={p.ok} label={p.name} detail={p.ok ? `Pristup porukama OK · ${p.threads} razgovora` : p.error} testId={`diag-prop-${p.channex_property_id}`} />
        ))}
      </div>
    </div>
  );
}
